import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | ok | error
  const [message, setMessage] = useState("");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState("error"); setMessage("Missing verification token."); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("ok");
        // Refresh auth so the banner disappears immediately for signed-in users.
        refresh().catch(() => {});
      } catch (e) {
        setState("error");
        const d = e?.response?.data?.detail;
        setMessage(typeof d === "string" ? d : "Verification failed.");
      }
    })();
  }, [token, refresh]);

  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <section className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full text-center border border-border bg-white p-10">
          {state === "loading" && (
            <>
              <div className="text-xs font-mono tracking-widest-2 uppercase text-muted-foreground mb-3">Verifying</div>
              <h1 className="font-heading text-2xl mb-3">Confirming your email…</h1>
              <p className="text-sm text-muted-foreground">This will only take a second.</p>
            </>
          )}
          {state === "ok" && (
            <>
              <CheckCircle size={40} weight="fill" className="mx-auto text-primary mb-4" />
              <h1 className="font-heading text-2xl mb-3">Email verified.</h1>
              <p className="text-sm text-muted-foreground mb-6">You can now receive order updates and secure download links at this address.</p>
              <Button
                onClick={() => navigate(user ? "/dashboard" : "/login")}
                className="rounded-sm"
                data-testid="verify-email-continue-btn"
              >
                {user ? "Go to dashboard" : "Sign in"}
              </Button>
            </>
          )}
          {state === "error" && (
            <>
              <WarningCircle size={40} weight="fill" className="mx-auto text-muted-foreground mb-4" />
              <h1 className="font-heading text-2xl mb-3">Verification failed.</h1>
              <p className="text-sm text-muted-foreground mb-6" data-testid="verify-email-error">{message}</p>
              <div className="flex gap-3 justify-center">
                <Link to={user ? "/dashboard" : "/login"}>
                  <Button variant="outline" className="rounded-sm">{user ? "Back to dashboard" : "Back to sign in"}</Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
